import React, { useCallback, useEffect, useState } from 'react';
import './FeedCatalog.css';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function FeedCatalog({ token, onImported }) {
  const [url, setUrl] = useState('');
  const [source, setSource] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const request = useCallback(async (path = '', method = 'GET', body) => {
    const response = await fetch(`${API_URL}/api/catalog-feed${path}`, {
      method, headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.detail || 'Не удалось загрузить фид');
    return data;
  }, [token]);
  const load = useCallback(async () => {
    try {
      const data = await request();
      setSource(data.source);
      if (data.source?.url) setUrl((current) => current || data.source.url);
      if (data.source?.status === 'ready' && onImported) onImported();
    } catch (e) { setError(e.message); }
  }, [request, onImported]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!['running', 'queued'].includes(source?.status)) return undefined;
    const timer = setInterval(load, 2000);
    return () => clearInterval(timer);
  }, [source?.status, load]);
  const run = async (action) => {
    setBusy(true); setError('');
    try { await action(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const running = ['running', 'queued'].includes(source?.status);
  return (
    <section className="feed-catalog">
      <h3>Каталог из YML-фида</h3>
      <p>Укажите ссылку на XML/YML-фид для Яндекс Маркета. Загрузим товары, категории и заполненные характеристики, включая товары под заказ.</p>
      {source && <div className="feed-catalog-source">
        <p className="feed-catalog-url">{source.url}</p>
        <p role="status">{running ? 'Загружаем каталог и подготавливаем новые товары…' : source.status === 'waiting_key' ? `Подготовлено товаров: ${source.products_count}. Эмбеддинги и анализ каталога начнутся после настройки ключа OpenAI.` : `Товаров: ${source.products_count}. ${source.enabled ? 'Обновление раз в час.' : 'Обновление отключено.'}`}</p>
        {source.error && <p role="alert" className="feed-catalog-error">{source.error}</p>}
        {!running && source.enabled && <>
          <button disabled={busy} type="button" onClick={() => run(async () => { await request('/refresh', 'POST'); await load(); })}>Обновить сейчас</button>
          <button disabled={busy} type="button" onClick={() => run(async () => { await request('', 'DELETE'); await load(); })}>Отключить обновление</button>
        </>}
      </div>}
      {!running && (!source || !source.enabled || source.status === 'error') && <>
        <label htmlFor="catalog-feed-url">Ссылка на фид</label>
        <input id="catalog-feed-url" type="url" placeholder="https://example.ru/catalog.xml" value={url}
          onChange={(e) => { setUrl(e.target.value); setPreview(null); }} disabled={busy} />
        <button disabled={busy || !url.trim()} type="button" onClick={() => run(async () => setPreview(await request('/preview', 'POST', { url: url.trim() })))}>
          {busy ? 'Загрузка…' : 'Проверить фид'}
        </button>
        {preview && <div className="feed-catalog-preview">
          <p><strong>{preview.products}</strong> товаров · {preview.categories.length} разделов · {preview.properties.length} характеристик</p>
          <p>С заполненными характеристиками: {preview.with_attributes}.</p>
          <details><summary>Характеристики и примеры значений</summary>
            <ul>{preview.properties.map((p) => <li key={p.name}><b>{p.name}</b>: {p.values.join(', ')}</li>)}</ul>
          </details>
          <p>Существующие эмбеддинги при обновлении сохраняются. Категории и характеристики предложит GPT; вы сможете проверить их перед началом работы.</p>
          <button disabled={busy} type="button" onClick={() => run(async () => { await request('/connect', 'POST', { url: url.trim(), digest: preview.digest }); await load(); })}>Подключить и загрузить товары</button>
        </div>}
      </>}
      {error && <p role="alert" className="feed-catalog-error">{error}</p>}
    </section>
  );
}
